<template>
  <div>
    <div v-for="t in toasts" :key="t.uid">
      <b-toast
        v-model="t.visible"
        toaster="toast-alertas-toaster"
        :variant="t.variant"
        solid
        no-auto-hide
        auto-hide-delay="0"
        @hidden="onHidden(t.uid)"
      >
        <template #toast-title>
            <div class="d-flex flex-grow-1 align-items-baseline">
            <b-img blank blank-color="#ff5555" class="mr-2" width="12" height="12"></b-img>
            <strong class="mr-auto">Atención</strong>
            <small class="text-muted mr-2">{{ formatTimestamp(t.timestamp) }}</small>
            </div>
        </template>
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h6 class="small mb-1" :style="{ color: t.textColor }"><strong>{{ t.cod_alerta }}:</strong> {{ t.titulo }}</h6>
            <div class="small" :style="{ color: t.textColor }"><strong>Ubicación:</strong> {{ t.ubicacion }}</div>
          </div>
        </div>

        <div class="w-100 d-flex justify-content-end">
          <b-button size="sm" variant="light" class="mr-2" @click="cerrarToast(t.uid)">Cerrar</b-button>
          <b-button size="sm" :variant="t.confirmVariant" @click="marcarLeido(t.uid, t.id)">Leído</b-button>
        </div>
      </b-toast>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'ToastAlertas',
  data() {
    return {
      toasts: [], // { uid, id, titulo, ubicacion, variant, textColor, confirmVariant, visible }
      alertasLeidasCooldown: new Map() // Map para almacenar alertas leídas con timestamp
    };
  },
  methods: {
    generarUid() {
      return Date.now().toString(36) + Math.random().toString(36).slice(2,8);
    },
    
    // ✅ Verificar si una alerta está en cooldown (24 horas)
    estaEnCooldown(alertaId) {
      if (!alertaId || !this.alertasLeidasCooldown.has(alertaId.toString())) return false;
      
      const timestampLeida = this.alertasLeidasCooldown.get(alertaId.toString());
      const ahora = Date.now();
      const unDiaEnMs = 24 * 60 * 60 * 1000; // 24 horas en milisegundos
      
      return (ahora - timestampLeida) < unDiaEnMs;
    },
    
    // ✅ Limpiar alertas expiradas del cooldown (más de 24 horas)
    limpiarCooldownExpirado() {
      const ahora = Date.now();
      const unDiaEnMs = 24 * 60 * 60 * 1000;
      
      for (const [alertaId, timestamp] of this.alertasLeidasCooldown.entries()) {
        if ((ahora - timestamp) >= unDiaEnMs) {
          this.alertasLeidasCooldown.delete(alertaId);
        }
      }
    },
    
    // ✅ Cargar cooldown desde localStorage
    cargarCooldownDesdeStorage() {
      try {
        const cooldownData = localStorage.getItem('alertasCooldown');
        if (cooldownData) {
          const data = JSON.parse(cooldownData);
          this.alertasLeidasCooldown = new Map(Object.entries(data));
          this.limpiarCooldownExpirado(); // Limpiar alertas expiradas al cargar
        }
      } catch (error) {
        console.error('Error al cargar cooldown de alertas:', error);
        this.alertasLeidasCooldown = new Map();
      }
    },
    
    // ✅ Guardar cooldown en localStorage
    guardarCooldownEnStorage() {
      try {
        const cooldownObj = Object.fromEntries(this.alertasLeidasCooldown);
        localStorage.setItem('alertasCooldown', JSON.stringify(cooldownObj));
      } catch (error) {
        console.error('Error al guardar cooldown de alertas:', error);
      }
    },
    pushAlertas(arr) {
        if (!Array.isArray(arr) || arr.length === 0) return;
        
        // ✅ No mostrar toasts en páginas de login
        const rutaActual = this.$route.path.toLowerCase();
        const rutasExcluidas = ['/sign-in', '/login', '/ingresar', '/recuperar-clave', '/reset-password', '/sign-up'];
        if (rutasExcluidas.some(ruta => rutaActual.includes(ruta))) {
          // console.log('🚫 [ToastAlertas] Bloqueando toast en página de login:', rutaActual);
          return;
        }
        
        // ✅ Filtrar alertas que están en cooldown (24 horas) O ya están marcadas como leídas
        const alertasFiltradas = arr.filter(a => {
          if (!a.id) return true; // Si no tiene ID, mostrar por seguridad
          
          // Verificar cooldown local
          const enCooldown = this.estaEnCooldown(a.id);
          if (enCooldown) {
            console.log(`🕒 [ToastAlertas] Alerta ${a.id} en cooldown, no se mostrará por 24h`);
            return false;
          }
          
          // Verificar si ya está marcada como leída en la base de datos
          if (a.raw && a.raw.leido === 1) {
            console.log(`📖 [ToastAlertas] Alerta ${a.id} ya está marcada como leída en BD, no se mostrará`);
            // También agregarla al cooldown para evitar mostrarla en el futuro
            this.alertasLeidasCooldown.set(a.id.toString(), Date.now());
            this.guardarCooldownEnStorage();
            return false;
          }
          
          return true;
        });
        
        if (alertasFiltradas.length === 0) {
          console.log('📵 [ToastAlertas] Todas las alertas están en cooldown');
          return;
        }
        
        const nuevos = alertasFiltradas.map(a => {
            const variant = (a.detallealerta_id === 7 || a.detallealerta_id === 5) ? 'danger' : (a.detallealerta_id === 6 ? 'warning' : 'info');
            const textColor = '#000';
            const confirmVariant = (a.detallealerta_id === 7 || a.detallealerta_id === 5) ? 'danger' : 'warning';
            const uid = this.generarUid();
            return {
                uid,
                id: a.id,
                titulo: a.titulo,
                ubicacion: a.ubicacion,
                variant,
                textColor,
                confirmVariant,
                visible: true,
                timestamp: Date.now(),
                cod_alerta: a.raw.cod_alerta || null,
                cbi: a.raw.cbi || null,
            };
        });

        this.toasts = nuevos;
    },
    cerrarToast(uid) {
      const idx = this.toasts.findIndex(t => t.uid === uid);
      if (idx !== -1) this.toasts[idx].visible = false;
    },
    formatTimestamp(ts) {
      const date = new Date(ts);
      return date.toLocaleString();
    },
    onHidden(uid) {
      const idx = this.toasts.findIndex(t => t.uid === uid);
      if (idx !== -1) this.toasts.splice(idx,1);
    },
    async marcarLeido(uid, id) {
      try {
        if (id) {
          // ✅ Marcar en la API como leído
          await axios.post('/api/actualizar-popup-alerta-actividades', { id }, {
            headers: { Authorization: `Bearer ${this.$store.state.user?.token || ''}` }
          });
          
          // ✅ Agregar al cooldown local por 24 horas
          this.alertasLeidasCooldown.set(id.toString(), Date.now());
          this.guardarCooldownEnStorage();
          
          // ✅ Notificar al sistema de notificaciones Windows que la alerta fue leída
          window.dispatchEvent(new CustomEvent('alerta-marcada-leida', { 
            detail: { alertaId: id, tipo: 'toast' }
          }));
          
          // ✅ Cerrar otros toasts con la misma alerta si existen
          this.cerrarToastsPorId(id);
          
          console.log(`✅ [ToastAlertas] Alerta ${id} marcada como leída, cooldown de 24h activado`);
        }
      } catch (err) {
        console.error('Error al marcar alerta como leída:', err);
      } finally {
        this.cerrarToast(uid);
      }
    },

    // ✅ Cerrar todos los toasts que tengan la misma alerta ID
    cerrarToastsPorId(alertaId) {
      if (!alertaId) return;
      
      const toastsAEliminar = this.toasts.filter(t => t.id === alertaId);
      toastsAEliminar.forEach(t => {
        console.log(`🚫 [ToastAlertas] Cerrando toast duplicado para alerta ${alertaId}`);
        this.cerrarToast(t.uid);
      });
    },

    // ✅ Verificar y cerrar toasts que ya están marcados como leídos
    verificarYCerrarToastsLeidos() {
      const toastsAEliminar = [];
      
      this.toasts.forEach(toast => {
        if (toast.id && this.estaEnCooldown(toast.id)) {
          toastsAEliminar.push(toast.uid);
          console.log(`📖 [ToastAlertas] Cerrando toast ${toast.uid} - alerta ${toast.id} ya está en cooldown`);
        }
      });
      
      // Cerrar los toasts que están en cooldown
      toastsAEliminar.forEach(uid => this.cerrarToast(uid));
    },
    _debugImprimirToast() {
        setTimeout(() => {
            const toastEl = document.querySelector('.b-toast');
            if (!toastEl) return; // console.log('[debug-toast] no existe .b-toast en DOM');
            // console.log('[debug-toast] outerHTML:', toastEl.outerHTML);
            const cs = window.getComputedStyle(toastEl);
            // console.log('[debug-toast] computed style:', {
            //     display: cs.display,
            //     position: cs.position,
            //     top: cs.top,
            //     right: cs.right,
            //     left: cs.left,
            //     bottom: cs.bottom,
            //     zIndex: cs.zIndex,
            //     opacity: cs.opacity,
            //     visibility: cs.visibility,
            //     transform: cs.transform,
            //     height: cs.height,
            //     width: cs.width
            // });
        }, 200);
    }
  },
  mounted() {
    // ✅ Cargar cooldown desde localStorage al montar el componente
    this.cargarCooldownDesdeStorage();
    
    this._toastHandler = (e) => {
        this.pushAlertas(e.detail || []);
    };
    window.addEventListener('toast-alertas', this._toastHandler);
    
    // ✅ Limpiar cooldown expirado cada hora
    this._cleanupInterval = setInterval(() => {
      this.limpiarCooldownExpirado();
      this.guardarCooldownEnStorage();
    }, 60 * 60 * 1000); // Cada hora
    
    // ✅ Verificar y cerrar toasts leídos cada 10 segundos
    this._verificarToastsInterval = setInterval(() => {
      this.verificarYCerrarToastsLeidos();
    }, 10 * 1000); // Cada 10 segundos
    
    // ✅ Escuchar cambios en localStorage para sincronización entre pestañas
    this._storageHandler = (e) => {
      if (e.key === 'alertasCooldown') {
        console.log('🔄 [ToastAlertas] Detectado cambio en cooldown desde otra pestaña');
        this.cargarCooldownDesdeStorage();
        this.verificarYCerrarToastsLeidos();
      }
    };
    window.addEventListener('storage', this._storageHandler);
    
    console.log('✅ [ToastAlertas] Componente montado con sistema de cooldown de 24h y sincronización');
  },
  beforeDestroy() {
    window.removeEventListener('toast-alertas', this._toastHandler);
    window.removeEventListener('storage', this._storageHandler);
    
    // ✅ Limpiar intervalos
    if (this._cleanupInterval) {
      clearInterval(this._cleanupInterval);
    }
    if (this._verificarToastsInterval) {
      clearInterval(this._verificarToastsInterval);
    }
  },
};
</script>

<style>

</style>